// src/components/schedules/SchedulesReports.tsx
import { useState } from "react";
import { Plus, Pencil, Trash2, Play, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useReportList,
  useCreateReportListItem,
  useUpdateReportListItem,
  useDeleteReportListItem,
  useExecuteReport,
} from "@/hooks/useReportDelivery";
import { ReportListItem, CreateReportListItem } from "@/types/reportDelivery";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

export function SchedulesReports() {
  const { data: reports, isLoading } = useReportList();
  const createReport = useCreateReportListItem();
  const updateReport = useUpdateReportListItem();
  const deleteReport = useDeleteReportListItem();
  const executeReport = useExecuteReport();

  const [showDialog, setShowDialog] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportListItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [executingReportId, setExecutingReportId] = useState<number | null>(null);

  const [formData, setFormData] = useState<CreateReportListItem>({
    name: "",
    description: "",
    sql: "",
    procedure: "",
    code: "",
    active: true,
  });

  const handleOpenDialog = (report?: ReportListItem) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        name: report.name,
        description: report.description || "",
        sql: report.sql || "",
        procedure: report.procedure || "",
        code: report.code || "",
        active: report.active === 1,
      });
    } else {
      setEditingReport(null);
      setFormData({
        name: "",
        description: "",
        sql: "",
        procedure: "",
        code: "",
        active: true,
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingReport(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingReport) {
        await updateReport.mutateAsync({
          reportID: editingReport.reportID,
          data: formData,
        });
      } else {
        await createReport.mutateAsync(formData);
      }
      handleCloseDialog();
    } catch {
      // Toast géré dans le hook
    }
  };

  const handleDelete = async (reportID: number) => {
    try {
      await deleteReport.mutateAsync(reportID);
      setDeleteConfirm(null);
    } catch {
      // Toast géré dans le hook
    }
  };

  const handleExecute = async (reportID: number) => {
    try {
      setExecutingReportId(reportID);
      await executeReport.mutateAsync(reportID);
    } catch {
      // Toast géré dans le hook
    } finally {
      setExecutingReportId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Rapports</CardTitle>
            <CardDescription>Gérez les rapports disponibles pour l'envoi automatique</CardDescription>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau rapport
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement...</div>
        ) : !reports || reports.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Aucun rapport configuré. Créez-en un pour commencer.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.reportID}>
                  <TableCell className="font-medium">
                    <div>
                      {report.name}
                      {report.description && (
                        <p className="text-sm text-muted-foreground">{report.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{report.code || "—"}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {report.sql ? "SQL" : report.procedure ? "Procédure" : "Non défini"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {report.active === 1 ? (
                      <Badge variant="default" className="bg-green-500">Actif</Badge>
                    ) : (
                      <Badge variant="secondary">Inactif</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleExecute(report.reportID)}
                        disabled={executingReportId !== null}
                        title="Exécuter maintenant"
                      >
                        {executingReportId === report.reportID ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(report)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteConfirm(report.reportID)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog Créer/Éditer */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReport ? "Modifier le rapport" : "Nouveau rapport"}</DialogTitle>
            <DialogDescription>
              Configurez les détails du rapport. Vous devez fournir soit une requête SQL, soit une procédure stockée.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code unique</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: MONTHLY_SALES"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sql">Requête SQL</Label>
                <Textarea
                  id="sql"
                  value={formData.sql}
                  onChange={(e) => setFormData({ ...formData, sql: e.target.value })}
                  placeholder="SELECT * FROM ..."
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="procedure">Procédure stockée</Label>
                <Input
                  id="procedure"
                  value={formData.procedure}
                  onChange={(e) => setFormData({ ...formData, procedure: e.target.value })}
                  placeholder="Ex: dbo.sp_GetReport"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Rapport actif</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={createReport.isPending || updateReport.isPending}
              >
                {createReport.isPending || updateReport.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmation suppression */}
      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce rapport ? Cette action supprimera également
              tous les destinataires et plannings associés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

